import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { Modal } from '@mui/material'
import BadgeIcon from '@mui/icons-material/Badge'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import GroupIcon from '@mui/icons-material/Group'
import CloseIcon from '@mui/icons-material/Close'
import CampaignIcon from '@mui/icons-material/Campaign'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism'
import membershipService from 'features/memberships/membershipService'
import SpinnerSmall from 'components/SpinnerSmall'
import DestructiveConfirmModal from 'components/modals/DestructiveConfirmModal'
import { useTranslation } from 'react-i18next'

const ROLE_LIMIT = 10

export default function MemberRolesPanel() {
  const { t } = useTranslation('settings')
  const { user } = useSelector((state) => state.auth)
  const token = user?.token
  const churchId = user?._id
  const [roles, setRoles] = useState([])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedRole, setSelectedRole] = useState(null)
  const [roleMembers, setRoleMembers] = useState([])
  const [roleMembersLoading, setRoleMembersLoading] = useState(false)
  const [removingMemberId, setRemovingMemberId] = useState('')
  const [removingRoleId, setRemovingRoleId] = useState('')
  const [confirmAction, setConfirmAction] = useState(null)

  const activeCount = roles.filter((role) => role.isActive).length

  const loadRoles = async () => {
    if (!churchId || !token) return
    setLoading(true)
    try {
      const data = await membershipService.getMinistryRoles(churchId, token)
      setRoles(data.ministryRoles || [])
    } catch (error) {
      toast.error(t('roles.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRoles()
  }, [churchId, token])

  const createRole = async () => {
    const trimmed = name.trim()
    if (!trimmed) return toast.error(t('roles.nameRequired'))
    if (activeCount >= ROLE_LIMIT) return toast.error(t('roles.limit'))

    setSaving(true)
    try {
      const data = await membershipService.createMinistryRole(churchId, {
        name: trimmed,
        description: description.trim(),
      }, token)
      setRoles((current) => [...current, data.ministryRole].sort((a, b) => a.name.localeCompare(b.name)))
      setName('')
      setDescription('')
      toast.success(t('roles.created'))
    } catch (error) {
      toast.error(error?.response?.data?.message || t('roles.createFailed'))
    } finally {
      setSaving(false)
    }
  }

  const requestDeactivateRole = (role, event) => {
    event.stopPropagation()
    setConfirmAction({ type: 'role', role })
  }

  const deactivateRole = async () => {
    const role = confirmAction?.role
    if (!role) return
    setRemovingRoleId(role.id)
    try {
      await membershipService.deactivateMinistryRole(churchId, role.id, token)
      setRoles((current) => current.filter((item) => item.id !== role.id))
      toast.success(t('roles.removed'))
      setConfirmAction(null)
    } catch (error) {
      toast.error(t('roles.removeFailed'))
    } finally {
      setRemovingRoleId('')
    }
  }

  const openRoleMembers = async (role) => {
    setSelectedRole(role)
    setRoleMembers([])
    setRoleMembersLoading(true)
    try {
      const data = await membershipService.getMinistryRoleMembers(churchId, role.id, token)
      setSelectedRole(data.role || role)
      setRoleMembers(data.members || [])
    } catch (error) {
      toast.error(t('roles.membersLoadFailed'))
    } finally {
      setRoleMembersLoading(false)
    }
  }

  const requestRemoveMemberFromRole = (member) => {
    setConfirmAction({ type: 'member', member, role: selectedRole })
  }

  const removeMemberFromRole = async () => {
    const member = confirmAction?.member
    const role = confirmAction?.role
    const memberId = member.id || member._id
    if (!role || !memberId) return

    setRemovingMemberId(memberId)
    try {
      await membershipService.removeMinistryRoleMember(churchId, role.id, memberId, token)
      setRoleMembers((current) => current.filter((item) => String(item.id || item._id) !== String(memberId)))
      setRoles((current) => current.map((item) => item.id === role.id
        ? { ...item, memberCount: Math.max(0, Number(item.memberCount || 0) - 1) }
        : item
      ))
      setSelectedRole((current) => current
        ? { ...current, memberCount: Math.max(0, Number(current.memberCount || 0) - 1) }
        : current
      )
      setConfirmAction(null)
    } catch (error) {
      toast.error(t('roles.memberRemoveFailed'))
    } finally {
      setRemovingMemberId('')
    }
  }

  return (
    <Panel>
      <PanelHeader>
        <TitleWrap>
          <BadgeIcon />
          <div>
            <Title>{t('roles.title')}</Title>
            <SubTitle>{t('roles.count', { count: activeCount, limit: ROLE_LIMIT })}</SubTitle>
          </div>
        </TitleWrap>
      </PanelHeader>

      <RoleIntro>
        {t('roles.intro')}
      </RoleIntro>

      <UseCaseGrid>
        <UseCaseItem>
          <CampaignIcon />
          <span>{t('roles.updates')}</span>
        </UseCaseItem>
        <UseCaseItem>
          <EventAvailableIcon />
          <span>{t('roles.events')}</span>
        </UseCaseItem>
        <UseCaseItem>
          <VolunteerActivismIcon />
          <span>{t('roles.involvement')}</span>
        </UseCaseItem>
      </UseCaseGrid>

      <CreateRoleCard>
        <CreateRoleHeader>
          <CreateRoleTitle>{t('roles.add')}</CreateRoleTitle>
          <CreateRoleHelp>{t('roles.addHelp')}</CreateRoleHelp>
        </CreateRoleHeader>
        <CreateRow>
          <FieldGroup>
            <FieldLabel>{t('roles.name')}</FieldLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('roles.namePlaceholder')} maxLength={80} />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>{t('roles.description')}</FieldLabel>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('roles.descriptionPlaceholder')} maxLength={500} />
          </FieldGroup>
          <CreateButton onClick={createRole} disabled={saving || activeCount >= ROLE_LIMIT}>
            {saving ? <SpinnerSmall /> : t('roles.create')}
          </CreateButton>
        </CreateRow>
      </CreateRoleCard>

      <RolesList>
        {loading ? (
          <LoadingRow><SpinnerSmall /></LoadingRow>
        ) : roles.length === 0 ? (
          <EmptyText>{t('roles.empty')}</EmptyText>
        ) : roles.map((role) => (
          <RoleItem key={role.id} onClick={() => openRoleMembers(role)}>
            <RoleIcon><BadgeIcon fontSize="small" /></RoleIcon>
            <RoleMain>
              <RoleName>{role.name}</RoleName>
              {role.description && <RoleDescription>{role.description}</RoleDescription>}
              <RoleMeta><GroupIcon fontSize="small" /> {t('roles.memberCount', { count: Number(role.memberCount || 0) })}</RoleMeta>
            </RoleMain>
            <IconButton onClick={(event) => requestDeactivateRole(role, event)} title={t('roles.remove')} disabled={removingRoleId === role.id}>
              {removingRoleId === role.id ? <SpinnerSmall /> : <DeleteOutlineIcon fontSize="small" />}
            </IconButton>
          </RoleItem>
        ))}
      </RolesList>

      <Modal open={!!selectedRole} onClose={() => setSelectedRole(null)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <MembersModal>
          <ModalHeader>
            <div>
              <ModalTitle>{selectedRole?.name || t('roles.fallback')}</ModalTitle>
              <SubTitle>{t('roles.assignedCount', { count: roleMembers.length })}</SubTitle>
            </div>
            <CloseButton onClick={() => setSelectedRole(null)}><CloseIcon /></CloseButton>
          </ModalHeader>

          <MemberList>
            {roleMembersLoading ? (
              <LoadingRow><SpinnerSmall /></LoadingRow>
            ) : roleMembers.length === 0 ? (
              <EmptyText>{t('roles.emptyMembers')}</EmptyText>
            ) : roleMembers.map((member) => {
              const memberId = member.id || member._id
              const img = member.profileImg?.urlSmall || member.profileImg?.url
              return (
                <MemberRow key={memberId}>
                  {img ? <Avatar src={img} alt="" /> : <AvatarFallback>{(member.fullName || member.username || 'M').slice(0, 1)}</AvatarFallback>}
                  <MemberInfo>
                    <RoleName>{member.fullName || member.username || t('roles.memberFallback')}</RoleName>
                    <RoleMeta>{member.email || t('roles.emailUnavailable')}</RoleMeta>
                  </MemberInfo>
                  <IconButton onClick={() => requestRemoveMemberFromRole(member)} disabled={removingMemberId === memberId}>
                    {removingMemberId === memberId ? <SpinnerSmall /> : <DeleteOutlineIcon fontSize="small" />}
                  </IconButton>
                </MemberRow>
              )
            })}
          </MemberList>
        </MembersModal>
      </Modal>

      <DestructiveConfirmModal
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        onConfirm={confirmAction?.type === 'role' ? deactivateRole : removeMemberFromRole}
        loading={Boolean(removingRoleId || removingMemberId)}
        title={confirmAction?.type === 'role' ? `Remove ${confirmAction?.role?.name || 'this role'}?` : 'Remove this member from the role?'}
        description={confirmAction?.type === 'role'
          ? 'Members assigned to this role will lose the role. This does not remove them from the church.'
          : `${confirmAction?.member?.fullName || confirmAction?.member?.username || 'This member'} will no longer have the ${confirmAction?.role?.name || 'selected'} role.`
        }
        confirmLabel={confirmAction?.type === 'role' ? 'Remove Role' : 'Remove Member'}
      />
    </Panel>
  )
}

const Panel = styled.div`
  width: calc(100% - 40px);
  max-width: 980px;
  background: #fff;
  border: 1px solid #e8edf2;
  border-radius: 14px;
  padding: 18px;
  margin: 0 auto 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: calc(100% - 20px);
    padding: 14px;
    border-radius: 12px;
  }
`
const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`
const TitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--lightThemeFontColor);
`
const Title = styled.div`
  font-size: 16px;
  font-weight: 700;
`
const SubTitle = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 2px;
`
const RoleIntro = styled.p`
  margin: 0 0 12px;
  color: var(--dFontColor);
  font-size: 13px;
  line-height: 1.45;
  text-align: left;
`
const UseCaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`
const UseCaseItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  padding: 10px;
  border-radius: 10px;
  background: rgba(0, 180, 204, 0.06);
  border: 1px solid rgba(0, 180, 204, 0.14);
  color: var(--dFontColor);
  font-size: 12px;
  line-height: 1.35;
  text-align: left;

  svg {
    color: var(--themeColor);
    font-size: 18px;
    flex-shrink: 0;
  }
`
const CreateRoleCard = styled.div`
  padding: 12px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #edf2f7;
  margin-bottom: 16px;
`
const CreateRoleHeader = styled.div`
  margin-bottom: 10px;
  text-align: left;
`
const CreateRoleTitle = styled.div`
  color: var(--lightThemeFontColor);
  font-size: 14px;
  font-weight: 800;
`
const CreateRoleHelp = styled.div`
  margin-top: 3px;
  color: #777;
  font-size: 12px;
  line-height: 1.35;
`
const CreateRow = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 1fr) minmax(220px, 1.35fr) auto;
  gap: 10px;
  align-items: end;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`
const FieldGroup = styled.label`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  text-align: left;
`
const FieldLabel = styled.span`
  color: var(--dFontColor);
  font-size: 12px;
  font-weight: 700;
`
const Input = styled.input`
  border: 1px solid #d8e0e8;
  border-radius: 8px;
  padding: 11px 12px;
  outline: none;
  background: #fff;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    border-color: var(--themeColor);
    box-shadow: 0 0 0 3px rgba(0,180,204,.1);
  }
`
const CreateButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  background: var(--themeColor);
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  min-height: 40px;
  &:disabled { opacity: .5; cursor: not-allowed; }
`
const RolesList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`
const RoleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #edf2f7;
  cursor: pointer;
  min-width: 0;
  box-sizing: border-box;
  &:hover {
    background: #f3f7fb;
    border-color: #d8e0e8;
  }
`
const RoleIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--themeColor);
  background: rgba(0,180,204,.12);
`
const RoleMain = styled.div`
  flex: 1;
  min-width: 0;
`
const RoleName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--lightThemeFontColor);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const RoleDescription = styled.div`
  margin-top: 3px;
  color: #777;
  font-size: 12px;
  line-height: 1.35;
  overflow-wrap: anywhere;
`
const RoleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #777;
  font-size: 12px;
  margin-top: 3px;
  min-width: 0;
`
const IconButton = styled.button`
  border: none;
  border-radius: 8px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d33;
  background: rgba(220, 0, 0, .08);
  cursor: pointer;
  &:disabled { opacity: .5; cursor: not-allowed; }
`
const EmptyText = styled.div`
  color: #777;
  font-size: 13px;
  padding: 10px;
`
const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`
const MembersModal = styled.div`
  width: 520px;
  max-width: 92vw;
  max-height: 82vh;
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  display: flex;
  flex-direction: column;
`
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`
const ModalTitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: var(--lightThemeFontColor);
`
const CloseButton = styled.button`
  border: none;
  border-radius: 50%;
  width: 34px;
  height: 34px;
  cursor: pointer;
`
const MemberList = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const MemberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #f8f8f8;
  border-radius: 10px;
`
const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`
const AvatarFallback = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,180,204,.12);
  color: var(--themeColor);
  font-weight: 800;
`
const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
`
